import { BaseComponent } from './BaseComponent.js';
import { DOMHelpers } from '../utils/DOMHelpers.js';
import { Formatters } from '../utils/Formatters.js';

/**
 * Message
 * Chat message component (AI or User)
 */
export class Message extends BaseComponent {
    constructor(sender, text, options = {}) {
        super();
        this.sender = sender; // 'ai' or 'user'
        this.text = text;
        this.options = {
            quickReplies: options.quickReplies || [],
            timestamp: options.timestamp || new Date(),
            onQuickReply: options.onQuickReply || null,
            ...options
        };
    }

    render() {
        const messageDiv = DOMHelpers.createElement('div', {
            className: `message ${this.sender}`,
            dataset: {
                sender: this.sender,
                timestamp: this.options.timestamp.toISOString()
            }
        });

        // Avatar
        const avatar = DOMHelpers.createElement('div', {
            className: 'message-avatar'
        }, [this.sender === 'ai' ? '🤖' : '👤']);

        // Content
        const content = DOMHelpers.createElement('div', {
            className: 'message-content'
        });

        // Parse text for bold markdown
        const parsedText = Formatters.parseBold(this.text);
        content.innerHTML = parsedText;

        // Add quick replies if present
        if (this.options.quickReplies && this.options.quickReplies.length > 0) {
            const repliesDiv = this._renderQuickReplies();
            content.appendChild(repliesDiv);
        }

        messageDiv.appendChild(avatar);
        messageDiv.appendChild(content);

        this.element = messageDiv;
        return messageDiv;
    }

    _renderQuickReplies() {
        const repliesDiv = DOMHelpers.createElement('div', {
            className: 'quick-replies'
        });

        this.options.quickReplies.forEach(reply => {
            const btn = DOMHelpers.createElement('button', {
                className: 'quick-reply-btn',
                onClick: () => {
                    if (this.options.onQuickReply) {
                        this.options.onQuickReply(reply);
                    }
                }
            }, [reply]);

            repliesDiv.appendChild(btn);
        });

        return repliesDiv;
    }

    /**
     * Remove quick replies from message
     */
    removeQuickReplies() {
        if (!this.element) return;
        const repliesDiv = this.element.querySelector('.quick-replies');
        if (repliesDiv) {
            repliesDiv.remove();
        }
    }

    /**
     * Add typing indicator
     */
    showTyping() {
        if (!this.element) return;
        const content = this.element.querySelector('.message-content');
        const typingDiv = DOMHelpers.createElement('div', {
            className: 'typing-dots'
        }, [
            DOMHelpers.createElement('span', { className: 'dot' }),
            DOMHelpers.createElement('span', { className: 'dot' }),
            DOMHelpers.createElement('span', { className: 'dot' })
        ]);
        content.appendChild(typingDiv);
    }

    /**
     * Static factory: Create AI message
     */
    static ai(text, quickReplies = [], onQuickReply = null) {
        return new Message('ai', text, { quickReplies, onQuickReply });
    }

    /**
     * Static factory: Create user message
     */
    static user(text) {
        return new Message('user', text);
    }

    /**
     * Static factory: Create system message
     */
    static system(text) {
        const msg = new Message('ai', text);
        msg.isSystem = true;
        return msg;
    }
}
